/**
 * Reading a stored message body without running it.
 *
 * A message body is whatever another person (or an agent) saved into
 * task_comments. It is sanitized on the way to the screen (CommentBody), but
 * the Messages page also reads bodies for other reasons: the room list's
 * preview line, the links a preview card is drawn for, the "Make it a task"
 * title. Parsing those by assigning a body to the innerHTML of a live
 * document's div is NOT inert: the browser starts loading an <img> the moment
 * it is parsed, even in a detached div, so `<img src=x onerror=...>` runs its
 * handler in the reader's session. DOMParser builds a separate document with
 * scripting off, so nothing in it loads or runs.
 */

function inertBody(html: string): HTMLElement {
  return new DOMParser().parseFromString(html, 'text/html').body;
}

/** The visible text of a body, trimmed. */
export function inertText(html: string): string {
  return (inertBody(html).textContent ?? '').trim();
}

/** Every distinct non-empty href in a body, in order of appearance. */
export function inertLinks(html: string): string[] {
  const hrefs: string[] = [];
  for (const anchor of Array.from(inertBody(html).querySelectorAll('a[href]'))) {
    const href = anchor.getAttribute('href');
    if (href !== null && href !== '' && !hrefs.includes(href)) {
      hrefs.push(href);
    }
  }
  return hrefs;
}

/** Text for interpolation into an HTML string: & < > " ' escaped. */
export function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
